import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BlogProgressBar from '../../../components/feature/BlogProgressBar';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import BlogRelatedStrip from '../../../components/feature/BlogRelatedStrip';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const CANONICAL = `${SITE_URL}/blog/life-insurance-stay-at-home-parents-indiana`;
const OG_IMAGE = 'https://public.readdy.ai/ai/img_res/0597611301f07ca41a213159d73ffa0f.jpg';

const economicValueTasks = [
  { task: 'Full-time childcare', hoursPerWeek: 40, hourlyRate: '$15.00', annualValue: '$31,200' },
  { task: 'Meal planning & cooking', hoursPerWeek: 14, hourlyRate: '$18.00', annualValue: '$13,104' },
  { task: 'Household management & cleaning', hoursPerWeek: 12, hourlyRate: '$20.00', annualValue: '$12,480' },
  { task: 'Transportation & errands', hoursPerWeek: 8, hourlyRate: '$15.00', annualValue: '$6,240' },
  { task: 'Educational support & tutoring', hoursPerWeek: 7, hourlyRate: '$25.00', annualValue: '$9,100' },
  { task: 'Medical care coordination', hoursPerWeek: 3, hourlyRate: '$22.00', annualValue: '$3,432' },
  { task: 'Financial & household admin', hoursPerWeek: 4, hourlyRate: '$20.00', annualValue: '$4,160' },
  { task: 'Overnight & weekend childcare', hoursPerWeek: 20, hourlyRate: '$12.00', annualValue: '$12,480' },
];

const indianaDaycareCosts = [
  { ageGroup: 'Infant (0–12 months)', centerBased: '$16,800/yr', inhomeNanny: '$28,000–$38,000/yr', indianaRank: '#18 most expensive state' },
  { ageGroup: 'Toddler (1–3 years)', centerBased: '$13,200/yr', inhomeNanny: '$24,000–$32,000/yr', indianaRank: 'Above national median' },
  { ageGroup: 'Preschool (3–5 years)', centerBased: '$10,800/yr', inhomeNanny: '$22,000–$28,000/yr', indianaRank: 'Average national rate' },
  { ageGroup: 'School-age (5–12 years)', centerBased: '$8,400/yr', inhomeNanny: '$18,000–$24,000/yr', indianaRank: 'Below national median' },
];

const rateTable = [
  { profile: 'Female, 27, Non-Smoker', term: '$500K / 20yr', monthly: '$17', annual: '$204' },
  { profile: 'Female, 30, Non-Smoker', term: '$750K / 20yr', monthly: '$26', annual: '$312' },
  { profile: 'Female, 33, Non-Smoker', term: '$500K / 20yr', monthly: '$21', annual: '$252' },
  { profile: 'Female, 35, Non-Smoker', term: '$750K / 20yr', monthly: '$33', annual: '$396' },
  { profile: 'Female, 38, Non-Smoker', term: '$500K / 20yr', monthly: '$29', annual: '$348' },
  { profile: 'Female, 42, Non-Smoker', term: '$500K / 20yr', monthly: '$44', annual: '$528' },
  { profile: 'Male, 30, Non-Smoker', term: '$750K / 20yr', monthly: '$36', annual: '$432' },
  { profile: 'Male, 35, Non-Smoker', term: '$1M / 20yr', monthly: '$58', annual: '$696' },
];

const policyOptions = [
  {
    icon: 'ri-time-line',
    color: 'bg-[#1A3A52]',
    name: '20-Year Term Life',
    best: 'Best overall choice for most SAHPs',
    description: 'Covers the critical childcare years (birth through college) at the lowest possible cost. A healthy 30-year-old Indiana mother can get $750K of coverage for under $30/month.',
    pros: ['Lowest premium per dollar of coverage', 'Covers the full childcare dependency window', 'Easy to get approved', 'Simple and straightforward'],
    cons: ['No cash value — pure protection only', 'Expires at end of term'],
    range: '$17–$44/mo',
    bestFor: 'Ages 25–40 with children under 10',
  },
  {
    icon: 'ri-shield-check-line',
    color: 'bg-[#D97642]',
    name: 'Whole Life (Permanent)',
    best: 'Lifetime coverage + forced savings',
    description: 'Builds guaranteed cash value over time that your family can borrow against tax-free. More expensive than term, but never expires and accumulates real value.',
    pros: ['Coverage never expires', 'Builds guaranteed cash value', 'Premiums never increase', 'Can fund college or supplement retirement'],
    cons: ['3–5× more expensive than term', 'Cash value grows slowly early on'],
    range: '$80–$200/mo',
    bestFor: 'Long-term financial planning focus',
  },
  {
    icon: 'ri-checkbox-circle-line',
    color: 'bg-emerald-600',
    name: 'No Medical Exam Term',
    best: 'Fast approval, no blood draws',
    description: 'Simplified-issue term life with same-day or next-day approval. Slightly higher premiums than fully underwritten term, but ideal for busy parents who want coverage now.',
    pros: ['No physical exam or blood draw', 'Approval in 24–48 hours', 'Coverage up to $500K available', 'Great for any health concerns'],
    cons: ['Slightly higher rates than fully underwritten', 'Lower maximum coverage limits'],
    range: '$25–$65/mo',
    bestFor: 'Busy parents who want fast coverage',
  },
];

const fiveCommonMistakes = [
  {
    number: '01',
    title: '"I don\'t earn income, so I don\'t need life insurance"',
    explanation: 'This is the most dangerous and widespread misconception in Indiana households. A stay-at-home parent provides $92,000–$184,000 worth of economic services per year. If they pass away, the surviving spouse must either leave work to care for children or pay market rates for childcare, cooking, transportation, and education support. Neither is free.',
    fix: 'Calculate your true replacement value using the task breakdown above. You likely need $500K–$1M in coverage.',
  },
  {
    number: '02',
    title: 'Only buying a small "rider" on a spouse\'s policy',
    explanation: 'Many life insurance policies offer a spousal rider for $10K–$50K, which sounds like a nice addition. But $30K covers less than 2 years of Indianapolis childcare. A rider is not a strategy — it\'s a band-aid on a broken arm.',
    fix: 'Purchase a separate, standalone policy for the stay-at-home parent. It\'s inexpensive and properly sized coverage.',
  },
  {
    number: '03',
    title: 'Waiting until "when I go back to work"',
    explanation: 'Life insurance rates are based on your age and health today — not your employment status or future income. Every year you wait, rates increase. A 28-year-old female gets $750K of 20-year term for $26/month. At 35, that same policy costs $33/month. At 40, it costs $50+/month. You lock in your rate when you apply.',
    fix: 'Apply now while rates are lowest. A licensed Indiana agent can get you approved and covered within a week.',
  },
  {
    number: '04',
    title: 'Underestimating how many years of coverage are needed',
    explanation: 'Many SAHPs buy a 10-year term assuming kids will be independent by then. But a 30-year-old with a 2-year-old and a newborn needs coverage until 2043 just to cover the youngest through high school. A 10-year policy expires when the youngest is only 12.',
    fix: 'Choose a term length that covers until your youngest child turns 22 or graduates college. For most Indiana families, that\'s a 20-year term minimum.',
  },
  {
    number: '05',
    title: 'Assuming the wage-earner\'s policy covers the whole family',
    explanation: 'The wage-earning spouse\'s policy is designed to replace their income. It is not designed to also fund $400,000+ in childcare and household services for the next 18 years. These are two separate financial needs that require two separate policies.',
    fix: 'Each parent needs their own policy sized to their specific economic contribution to the household.',
  },
];

export default function LifeInsuranceStayAtHomeParentsIndiana() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Set canonical URL
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = CANONICAL;

    document.title = 'Life Insurance for Stay-at-Home Parents Indiana: 2025 Guide | Hoosier Life Insurance';

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Life Insurance for Stay-at-Home Parents in Indiana: The $184K Reason You Need Coverage (2025)',
        description: 'Why Indiana stay-at-home parents are the most underinsured group in America, what your household services are truly worth, and how to get $500K+ in coverage for under $30/month.',
        author: { '@type': 'Person', name: 'Benjamin Wright' },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-03-22T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: CANONICAL,
        mainEntityOfPage: 'https://hoosierlifeinsurance.com/blog/life-insurance-stay-at-home-parents-indiana',
        image: { '@type': 'ImageObject', url: 'https://readdy.ai/api/search-image?query=stay%20at%20home%20parent%20Indiana%20mom%20playing%20with%20two%20young%20children%20in%20bright%20sunny%20living%20room%20warm%20natural%20light%20cozy%20family%20home%20clean%20minimal%20background%20loving%20nurturing%20family%20scene&width=1200&height=630&seq=blog-sahp-og&orientation=landscape', width: 1200, height: 630 },
        timeRequired: 'PT12M',
        wordCount: 2400,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', 'details summary']
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Can a stay-at-home parent in Indiana get life insurance without income?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. Life insurance carriers assess eligibility based on your economic value to the household — not just a W-2 income. Indiana stay-at-home parents qualify for coverage based on the insurable interest of the wage-earning spouse and the market value of household services. Most SAHPs qualify for $250K–$1M+ in coverage.',
            },
          },
          {
            '@type': 'Question',
            name: 'How much life insurance does a stay-at-home parent in Indiana need?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'At minimum, enough to replace your childcare, household management, and education support services until your youngest child turns 18. For a family with 2 children in Central Indiana, that typically means $500,000–$1,000,000 of coverage, depending on your children\'s ages and the local cost of replacement services.',
            },
          },
          {
            '@type': 'Question',
            name: 'How much does life insurance cost for a stay-at-home parent in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'A healthy non-smoking 30-year-old Indiana mother can get $750,000 of 20-year term life insurance for approximately $26/month. A 35-year-old gets the same coverage for approximately $33/month. Rates are generally lower for women than men due to longer average life expectancy.',
            },
          },
          {
            '@type': 'Question',
            name: 'Should a stay-at-home parent have their own separate policy or a rider on their spouse\'s policy?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'A separate policy is strongly recommended. Spousal riders typically only provide $10,000–$50,000 of coverage — far too little to replace years of childcare services. A standalone term policy can provide $500K+ of dedicated coverage for $20–$40/month, making it both affordable and properly sized for the actual financial risk.',
            },
          },
          {
            '@type': 'Question',
            name: 'What happens to the stay-at-home parent\'s life insurance if they return to work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Nothing changes — the policy remains in force as long as you continue paying premiums. You may want to review your coverage amount once you\'re earning income again, as your needs will have shifted. Some people increase coverage once employed; others find their existing term coverage is still appropriate.',
            },
          },
        ],
      },
    ];

    schemas.forEach((schema, idx) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = `schema-sahp-${idx}`;
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });

    return () => {
      schemas.forEach((_, idx) => document.getElementById(`schema-sahp-${idx}`)?.remove());
    };
  }, []);

  const totalWeeklyHours = economicValueTasks.reduce((acc, t) => acc + t.hoursPerWeek, 0);

  return (
    <>
      <title>Life Insurance for Stay-at-Home Parents Indiana: 2026 Guide | Hoosier Life</title>
      <meta name="description" content="Indiana stay-at-home parents provide $92K–$184K in annual household services. Learn how to calculate your true coverage need and get $750K of protection for under $30/month in 2026." />
      <meta name="keywords" content="life insurance stay at home parents Indiana, stay at home mom life insurance Indiana, SAHP life insurance Indianapolis, life insurance homemaker Indiana" />
      <meta property="og:title" content="Life Insurance for Stay-at-Home Parents Indiana: The $184K Reason You Need Coverage" />
      <meta property="og:description" content="Indiana SAHPs provide over $92K in annual household services. Here's why you need life insurance, how to calculate the right amount, and how to get covered for under $30/month." />
      <meta property="og:url" content={CANONICAL} />
      <link rel="canonical" href={CANONICAL} />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={OG_IMAGE} />

      <BlogProgressBar />
      <Header />
      <article className="min-h-screen bg-white">

        {/* Hero */}
        <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white pt-28 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center text-white/60 hover:text-[#D97642] mb-6 transition-colors text-sm cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-1.5 rounded-full">Family Coverage</span>
              <span className="text-white/50 text-xs">12 min read · March 22, 2025</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Life Insurance for Indiana Stay-at-Home Parents: Why Your $184,000 Role Needs Coverage</h1>
            <p className="text-white/75 text-xl leading-relaxed max-w-2xl">
              Indiana stay-at-home parents are the most underinsured group in America — because most people misunderstand what a SAHP is actually worth. This guide fixes that.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/instant-quote" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap cursor-pointer">Get My Quote</Link>
              <Link to="/book-appointment" className="px-6 py-3 bg-white/10 text-white rounded-lg font-semibold border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer">Talk to an Agent</Link>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">

          {/* Key stat alert */}
          <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl mb-10">
            <p className="font-bold text-emerald-800 mb-2 flex items-center gap-2">
              <i className="ri-alert-line text-emerald-500 text-lg"></i>
              The Hidden Salary No One Talks About
            </p>
            <p className="text-emerald-700 leading-relaxed text-[15px]">
              Salary.com estimates the economic value of a full-time stay-at-home parent at <strong>$184,820 per year</strong> in 2024 — a figure based on 18 job functions from childcare to financial management. In Indiana, even a conservative calculation puts the replacement cost at <strong>$92,000–$115,000 per year</strong>. Despite this, most Indiana SAHPs have either no coverage or a $25,000 spousal rider — less than 3 months of replacement services.
            </p>
          </div>

          {/* Key Takeaways */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-10">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'Indiana stay-at-home parents provide $92,000–$184,000 per year in household services — yet most carry zero life insurance or a $25,000 spousal rider that covers less than 2 years of Indianapolis daycare',
                'A healthy 30-year-old Indiana mother gets $750,000 of 20-year term life insurance for approximately $26/month — less than one month of center-based childcare in Indianapolis',
                'SAHP coverage should equal years until youngest turns 18 × ($13,500 childcare + $25,000 household services + $8,000 education support) — typically $500,000–$1,000,000 total',
                'Stay-at-home parents qualify for life insurance based on household economic value — employment status and W-2 income are NOT required to get substantial coverage',
                'A spousal rider ($10K–$50K) is not a coverage strategy — it covers less than 2 years of Indianapolis childcare and leaves the family financially exposed for the remaining 16+ years',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Economic Value Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">What Is an Indiana Stay-at-Home Parent Actually Worth?</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Salary.com calculates the full market value of a stay-at-home parent at $184,820/year nationally. In Indiana, using regional market rates, a conservative calculation puts the replacement cost at $92,000–$115,000 per year. If your youngest child is 3, that's 15 years × $92,000 = $1,380,000 in economic value your family would need to replace — making a $25,000 spousal rider dangerously inadequate.
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 mb-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    {['Service Provided', 'Hours/Week', 'Indiana Market Rate', 'Annual Value'].map(h => (
                      <th key={h} className="px-5 py-3 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {economicValueTasks.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-3 font-medium text-gray-900">{row.task}</td>
                      <td className="px-5 py-3 text-gray-600">{row.hoursPerWeek} hrs</td>
                      <td className="px-5 py-3 text-gray-600">{row.hourlyRate}/hr</td>
                      <td className="px-5 py-3 text-emerald-700 font-bold">{row.annualValue}</td>
                    </tr>
                  ))}
                  <tr className="bg-[#D97642] text-white">
                    <td className="px-5 py-3 font-bold">Total (Conservative Indiana Estimate)</td>
                    <td className="px-5 py-3 font-bold">{totalWeeklyHours} hrs/wk</td>
                    <td className="px-5 py-3"></td>
                    <td className="px-5 py-3 font-black text-lg">$92,196/yr</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500">*Indiana market rates based on 2025 regional averages. Salary.com national figure: $184,820/yr including overtime premiums.</p>

            <div className="mt-6 bg-[#1A3A52]/5 border border-[#1A3A52]/20 rounded-xl p-5">
              <p className="text-gray-700 text-sm leading-relaxed">
                <strong className="text-[#1A3A52]">The 15-year projection:</strong> If your youngest child is 3 years old today, you have 15 years of services ahead until they graduate high school. At $92,000/year, that's <strong>$1,380,000 in total economic value</strong> your family would need to replace. This is why the "$25,000 spousal rider" strategy is dangerously inadequate.
              </p>
            </div>
          </section>

          {/* Indiana Daycare Cost Reality */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">The Indiana Daycare Reality Check</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Indiana ranks #18 among the most expensive states for childcare. Center-based infant care costs $16,800/year; toddler care runs $13,200/year. A family with a 2-year-old and a 5-year-old would spend $21,000–$25,200/year in center-based childcare alone if the SAHP were gone — $420,000–$504,000 over 16 years for childcare alone, before accounting for cooking, cleaning, or tutoring.
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    {['Child\'s Age', 'Center-Based Care', 'In-Home Nanny', 'Market Position'].map(h => (
                      <th key={h} className="px-5 py-3 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {indianaDaycareCosts.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-3 font-medium text-gray-900">{row.ageGroup}</td>
                      <td className="px-5 py-3 text-red-600 font-semibold">{row.centerBased}</td>
                      <td className="px-5 py-3 text-red-700 font-semibold">{row.inhomeNanny}</td>
                      <td className="px-5 py-3 text-gray-500 text-xs">{row.indianaRank}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <p className="font-bold text-red-800 mb-2 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500"></i> Real Scenario: 2 Indiana Kids
                </p>
                <p className="text-red-700 text-sm leading-relaxed">
                  A family with a 2-year-old and a 5-year-old in Indianapolis would spend <strong>$21,000–$25,200/year</strong> in center-based childcare alone if the stay-at-home parent were gone. That's $420,000–$504,000 over the next 16 years — just for childcare, not cooking, tutoring, or transportation.
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                <p className="font-bold text-emerald-800 mb-2 flex items-center gap-2">
                  <i className="ri-shield-check-line text-emerald-500"></i> The Coverage Solution
                </p>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  A $750,000 term life policy for this family's SAHP costs approximately <strong>$26–$33/month</strong> for a healthy 30-35 year old Indiana woman. That's less than one month of daycare costs, securing 15+ years of your family's financial foundation.
                </p>
              </div>
            </div>
          </section>

          {/* How Much Coverage */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">How Much Life Insurance Does an Indiana SAHP Actually Need?</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> The SAHP-specific calculation: (1) Years until youngest turns 18 × Indiana daycare cost, (2) + Years until youngest turns 18 × $25,000 household services, (3) + Years of school-age children × $8,000 education support, (4) + 1–2 years of surviving spouse's income as adjustment buffer. For most Indiana SAHPs with children under 10, this totals $500,000–$1,000,000.
            </div>
            <p className="text-gray-600 mb-6">
              The calculation is different from a wage-earner's formula. Here's the SAHP-specific method used by experienced Indiana agents:
            </p>
            <div className="space-y-4">
              {[
                {
                  step: 'Step 1: Childcare Replacement',
                  formula: 'Years until youngest turns 18 × average Indiana daycare cost',
                  example: '15 years × $13,500/yr = $202,500',
                  note: 'Use the center-based cost for your youngest child\'s age. This covers the legally required supervised care period.',
                  color: 'border-[#1A3A52]',
                  bg: 'bg-[#1A3A52]/5',
                },
                {
                  step: 'Step 2: Household Services',
                  formula: 'Years until youngest turns 18 × $25,000 (cooking, cleaning, errands, household management)',
                  example: '15 years × $25,000 = $375,000',
                  note: 'This conservatively covers meals, cleaning, transportation, and household administration. The surviving spouse\'s work schedule may make hiring help unavoidable.',
                  color: 'border-[#D97642]',
                  bg: 'bg-[#D97642]/5',
                },
                {
                  step: 'Step 3: Educational Support',
                  formula: 'Years of school-age children × $8,000 (tutoring, enrichment, summer programs)',
                  example: '10 years × $8,000 = $80,000',
                  note: 'Once the surviving parent returns to full-time work, educational support often goes from free (SAHP\'s time) to paid tutors, after-school programs, and summer camps.',
                  color: 'border-amber-400',
                  bg: 'bg-amber-50',
                },
                {
                  step: 'Step 4: Wage-Earning Spouse Adjustment',
                  formula: 'If surviving spouse must reduce hours or take leave: add 1–2 years of their income',
                  example: '$85,000 salary × 1.5 years = $127,500',
                  note: 'Many surviving spouses take significant leave or reduce work hours in the year following a spouse\'s death. This buffer prevents financial collapse during the adjustment period.',
                  color: 'border-emerald-500',
                  bg: 'bg-emerald-50',
                },
              ].map((s, i) => (
                <div key={i} className={`border-l-4 ${s.color} ${s.bg} p-5 rounded-r-xl`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-1">{s.step}</h3>
                      <p className="text-gray-600 text-sm font-mono mb-1">{s.formula}</p>
                      <p className="text-gray-500 text-xs">{s.note}</p>
                    </div>
                    <span className="font-black text-gray-800 text-lg whitespace-nowrap">{s.example}</span>
                  </div>
                </div>
              ))}
              <div className="bg-[#D97642] rounded-xl p-5 flex items-center justify-between text-white">
                <div>
                  <p className="font-bold text-lg">Total Recommended SAHP Coverage</p>
                  <p className="text-white/75 text-sm">Example: Indiana SAHP, 2 kids (ages 3 &amp; 6), spouse earns $85K</p>
                </div>
                <span className="font-black text-2xl whitespace-nowrap">$785,000</span>
              </div>
            </div>

            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-xl p-5">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>Quick Rule of Thumb:</strong> Most Indiana stay-at-home parents with children under 12 should carry <strong>$500,000–$1,000,000</strong> in coverage. The upper end applies for families with 3+ children, children under age 5, or in higher-cost Indianapolis metro areas. For a broader look at coverage sizing for all Hoosier families, see our guide on{' '}
                <Link to="/blog/how-much-life-insurance-indiana" className="text-[#D97642] font-semibold hover:underline cursor-pointer">how much life insurance Indiana families need</Link>.
              </p>
            </div>
          </section>

          {/* Policy Options */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Best Life Insurance Options for Indiana SAHPs</h2>
            <p className="text-gray-600 mb-6">
              Most stay-at-home parents in Indiana will be well served by one of these three approaches. Your age, health, and long-term goals determine which is the best fit:
            </p>
            <div className="space-y-5">
              {policyOptions.map((p, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-2xl p-6 hover:border-[#D97642]/30 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 flex-shrink-0 ${p.color} rounded-xl flex items-center justify-center`}>
                      <i className={`${p.icon} text-white text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                        <div>
                          <h3 className="font-bold text-gray-900 text-lg">{p.name}</h3>
                          <p className="text-xs text-[#D97642] font-semibold mt-0.5">{p.best}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Typical monthly</p>
                          <p className="font-black text-[#1A3A52] text-xl">{p.range}</p>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{p.description}</p>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Advantages</p>
                          <ul className="space-y-1.5">
                            {p.pros.map((pro, j) => (
                              <li key={j} className="text-xs text-gray-700 flex items-start gap-2">
                                <i className="ri-check-line text-emerald-500 flex-shrink-0 mt-0.5"></i>{pro}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Considerations</p>
                          <ul className="space-y-1.5">
                            {p.cons.map((con, j) => (
                              <li key={j} className="text-xs text-gray-700 flex items-start gap-2">
                                <i className="ri-subtract-line text-amber-500 flex-shrink-0 mt-0.5"></i>{con}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="mt-3 inline-flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                        <i className="ri-user-line text-gray-400 text-2xl"></i>
                        <span className="text-xs text-gray-500">Best for: <strong>{p.bestFor}</strong></span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 p-5 bg-[#1A3A52]/5 border border-[#1A3A52]/20 rounded-xl">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong className="text-[#1A3A52]">Not sure which to choose?</strong> For most Indiana SAHPs in their late 20s to mid-30s, a 20-year term policy is the default right answer. It costs the least, covers the critical dependency window, and can always be supplemented later. Want to see the exact rate spread between term, no-exam, and whole life options in Indiana? Check our{' '}
                <Link to="/blog/cheapest-life-insurance-indiana" className="text-[#D97642] font-semibold hover:underline cursor-pointer">guide to the cheapest life insurance in Indiana</Link>.
              </p>
            </div>
          </section>

          {/* Rate Table */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Real 2025 Rate Samples for Indiana Stay-at-Home Parents</h2>
            <p className="text-gray-600 mb-6">
              These rates are for healthy, non-smoking Indiana residents applying for 20-year term life insurance in Preferred or Preferred Plus health class. Actual rates depend on full application:
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    {['Applicant Profile', 'Coverage &amp; Term', 'Monthly Premium', 'Annual Cost'].map(h => (
                      <th key={h} className="px-5 py-3 text-left font-semibold" dangerouslySetInnerHTML={{ __html: h }}></th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rateTable.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-3 font-medium text-gray-900">{r.profile}</td>
                      <td className="px-5 py-3 text-gray-700">{r.term}</td>
                      <td className="px-5 py-3 text-[#D97642] font-black text-lg">{r.monthly}/mo</td>
                      <td className="px-5 py-3 text-gray-500">{r.annual}/yr</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">*Preferred health class estimates, 2025. Actual rates vary by carrier and full health profile. Rates for women are often 20–25% lower than equivalent male rates.</p>

            <div className="mt-4 p-5 bg-[#D97642]/5 border border-[#D97642]/20 rounded-xl">
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong className="text-[#D97642]">The cost comparison that matters most:</strong> A 30-year-old Indiana mother paying $26/month for $750,000 of 20-year term coverage is paying <strong>less than 2 hours of Indianapolis daycare</strong> per month to protect her family from a $750,000 financial catastrophe. No other financial product offers this ratio of protection to cost. For the full carrier rate comparison, see our{' '}
                <Link to="/blog/life-insurance-rates-indianapolis" className="text-[#D97642] font-semibold hover:underline cursor-pointer">Indianapolis life insurance rates guide</Link>.
              </p>
            </div>
          </section>

          {/* Author Bio */}
          <div className="border border-gray-200 rounded-2xl p-7 my-8 flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
              <i className="ri-shield-user-line text-white text-2xl"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base mb-1">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">Our licensed Indiana agents specialize in helping Indianapolis families properly insure stay-at-home parents — often the most underinsured members of the household. We calculate true economic replacement value and find the right coverage amount and carrier for your family's situation.</p>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Licensed in Indiana</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Independent — 20+ Carriers</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Last reviewed March 2025</span>
              </div>
            </div>
          </div>

          {/* 5 Mistakes */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">5 Costly Mistakes Indiana SAHPs Make with Life Insurance</h2>
            <p className="text-gray-600 mb-6">These are the most common and damaging errors made by stay-at-home parents across Central Indiana — and how to avoid each one:</p>
            <div className="space-y-5">
              {fiveCommonMistakes.map((m, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="flex items-start gap-0">
                    <div className="bg-[#1A3A52] text-white px-5 py-5 flex-shrink-0 flex items-start justify-center w-16">
                      <span className="font-black text-2xl opacity-60">{m.number}</span>
                    </div>
                    <div className="p-5 flex-1">
                      <h3 className="font-bold text-gray-900 mb-2 text-[15px]">{m.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed mb-3">{m.explanation}</p>
                      <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5">
                        <i className="ri-check-double-line text-emerald-500 flex-shrink-0 mt-0.5"></i>
                        <p className="text-emerald-800 text-xs font-medium leading-relaxed">{m.fix}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Apply */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">How the Application Process Works for Indiana SAHPs</h2>
            <p className="text-gray-600 mb-6">
              Many stay-at-home parents in Indiana aren't sure if they'll qualify for significant coverage without a W-2. Here's exactly what the process looks like:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  icon: 'ri-file-list-3-line',
                  title: 'Qualification Basis',
                  body: 'Carriers evaluate SAHPs based on (1) household insurable interest — the economic value to the family unit, (2) the wage-earning spouse\'s income, and (3) the SAHP\'s age, health, and lifestyle. A spouse earning $80K/year typically supports up to $1.5M in SAHP coverage with no issues.',
                  color: 'text-[#1A3A52]',
                },
                {
                  icon: 'ri-stethoscope-line',
                  title: 'Medical Requirements',
                  body: 'For coverage under $500K, most Indiana SAHPs under age 40 qualify for accelerated underwriting — no blood draw, no physical exam. Just a health questionnaire. For $500K–$1M+ in coverage, a simple paramedical exam (30-minute in-home visit) is typically required.',
                  color: 'text-[#D97642]',
                },
                {
                  icon: 'ri-time-line',
                  title: 'How Long It Takes',
                  body: 'Accelerated underwriting: 24–72 hours for approval. Fully underwritten ($500K+): 2–4 weeks for the exam results and carrier decision. Guaranteed-issue products: same day. A licensed Indiana agent can guide you to the fastest path for your situation.',
                  color: 'text-emerald-600',
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  title: 'Who Pays the Premium',
                  body: 'Either spouse can pay. Many families pull the SAHP\'s premium directly from the household account. The policy is owned by the SAHP, with the spouse as primary beneficiary. This structure ensures the death benefit goes to the right person tax-free.',
                  color: 'text-amber-600',
                },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-gray-200`}>
                      <i className={`${item.icon} ${item.color} text-lg`}></i>
                    </div>
                    <h3 className="font-bold text-gray-900 text-[15px]">{item.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 p-5 bg-[#1A3A52]/5 border border-[#1A3A52]/20 rounded-xl">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong className="text-[#1A3A52]">Important:</strong> If you have any health conditions — even controlled ones like high blood pressure or borderline cholesterol — carrier selection matters enormously. See our guides on{' '}
                <Link to="/blog/life-insurance-high-blood-pressure" className="text-[#D97642] font-semibold hover:underline cursor-pointer">life insurance with high blood pressure in Indiana</Link>{' '}
                and{' '}
                <Link to="/blog/no-medical-exam-life-insurance-indiana" className="text-[#D97642] font-semibold hover:underline cursor-pointer">no-exam life insurance options in Indiana</Link>{' '}
                for health-specific approval strategies.
              </p>
            </div>
          </section>

          {/* Family Coverage Context */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">The Complete Indiana Family Coverage Picture</h2>
            <p className="text-gray-600 mb-5 leading-relaxed">
              A stay-at-home parent's policy is one piece of a comprehensive family coverage plan. Here's how all the pieces fit together for a typical Indianapolis family:
            </p>
            <div className="bg-[#1A3A52] rounded-2xl p-6 text-white">
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    label: 'Wage-Earning Spouse',
                    icon: 'ri-briefcase-4-line',
                    coverage: '$750K–$1.5M term',
                    purpose: 'Income replacement for 15+ working years',
                    monthly: '$45–$120/mo',
                  },
                  {
                    label: 'Stay-at-Home Parent',
                    icon: 'ri-home-heart-line',
                    coverage: '$500K–$1M term',
                    purpose: 'Service replacement (childcare, household, etc.)',
                    monthly: '$17–$44/mo',
                  },
                ].map((item, i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#D97642] rounded-lg">
                        <i className={`${item.icon} text-white text-lg`}></i>
                      </div>
                      <h3 className="font-bold text-lg">{item.label}</h3>
                    </div>
                    <p className="text-white/75 text-sm mb-1">Coverage: <strong className="text-white">{item.coverage}</strong></p>
                    <p className="text-white/75 text-sm mb-1">Purpose: {item.purpose}</p>
                    <p className="text-white/75 text-sm">Typical cost: <strong className="text-[#D97642]">{item.monthly}</strong></p>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-white/70 text-sm">Combined monthly cost for complete Indiana family coverage</p>
                  <span className="font-black text-[#D97642] text-2xl">$62–$164/mo</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">Both policies combined, healthy non-smoking couple ages 28–35. Less than a car payment for complete family protection.</p>

            <div className="mt-5 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-sm text-gray-600">
                For a deeper look at how Indiana families should structure their total coverage, including how to factor in mortgage balance and college funding, see our{' '}
                <Link to="/blog/life-insurance-for-families-indianapolis" className="text-[#D97642] font-semibold hover:underline cursor-pointer">complete guide to life insurance for Indianapolis families</Link>.
              </p>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                {
                  q: 'Can a stay-at-home parent in Indiana get life insurance without income?',
                  a: 'Yes — and many get substantial coverage. Life insurance carriers evaluate household economic value, not just W-2 income. Indiana SAHPs routinely qualify for $500K–$1M+ in term life coverage. The carrier looks at the wage-earning spouse\'s income, the household\'s insurable interest, and the SAHP\'s age and health.',
                },
                {
                  q: 'How much life insurance does a stay-at-home parent need in Indiana?',
                  a: 'For a family with children, the calculation includes childcare replacement (years until youngest turns 18 × Indiana daycare costs), household service replacement ($20,000–$30,000/year for cooking, cleaning, transportation), and educational support. For most Indiana families, this means $500,000–$1,000,000 in coverage is appropriate.',
                },
                {
                  q: 'Is it cheaper for a stay-at-home mom to get life insurance than a working mom?',
                  a: 'Rates are determined by age, gender, health, and coverage amount — not employment status. A 30-year-old stay-at-home mother and a 30-year-old working mother with identical health profiles will be quoted identical rates. SAHPs don\'t face a premium for being outside the workforce.',
                },
                {
                  q: 'Should a stay-at-home parent have a separate policy or just a rider on the spouse\'s plan?',
                  a: 'A separate standalone policy is strongly recommended. Spousal riders typically only provide $10,000–$50,000, which covers less than 2 years of Indiana childcare. A proper term policy can provide $500K+ for $20–$40/month. The price difference per dollar of coverage makes the standalone policy the clearly superior choice.',
                },
                {
                  q: 'What happens to the policy when the stay-at-home parent returns to work?',
                  a: 'The policy remains in force as long as premiums are paid. You may want to review coverage needs once back at work — your income replacement needs will increase, but childcare costs may shift. Many Hoosiers keep their SAHP-era term policy and simply add a new employer-linked policy once working. Rates are locked at application, so your early-applied rate stays.',
                },
              ].map((item, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-5">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                    {item.q}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed pl-6">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-center text-white mb-4">
            <div className="w-14 h-14 flex items-center justify-center bg-[#D97642]/20 rounded-full mx-auto mb-4">
              <i className="ri-home-heart-line text-[#D97642] text-2xl"></i>
            </div>
            <h2 className="text-2xl font-bold mb-3">Protect Everything You Build Every Day</h2>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">
              Get a free, no-obligation quote in under 5 minutes. A licensed Indiana agent will help you find the right coverage amount and policy type — specific to your family's situation.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/instant-quote" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap cursor-pointer">Get Instant Quote</Link>
              <Link to="/book-appointment" className="px-6 py-3 bg-white/10 text-white rounded-lg font-bold hover:bg-white/20 border border-white/20 transition-colors whitespace-nowrap cursor-pointer">Book Free Consultation</Link>
            </div>
          </section>

        </div>
      </article>

      <BlogRelatedStrip currentSlug="life-insurance-stay-at-home-parents-indiana" />
      <Footer />
    </>
  );
}
